<script setup>
    import ClientImage from '@/components/client/Image';
    import { ref, reactive, watch } from 'vue';
    import SecondaryButton from '@/components/client/Buttons/SecondaryButton';
    import InputField from '@/components/client/Inputs/Input';
    import HtmlEditorField from '@/components/client/Inputs/HtmlEditorInput';
    import Popper from 'vue3-popper';

    const props = defineProps({
        image: [Object, null],
        heading: [String, null],
        desc: [String, null],
        errors: Object,
    });

    const emits = defineEmits(['update:image', 'update:heading', 'update:desc', 'errorResolved']);
    const mainImageRef = ref();

    const _image = props.image;
    const _heading = ref(props.heading);
    const _desc = ref(props.desc);

    function onCrop(c) {
        // Only change the image if a new image has been uploaded.
        if (c.image) {
            _image.image = c.image;
        }

        _image.image_crop = c.image_crop;
        _image.file = c.file;
        _image.cropper = c.cropper;
        _image.deleted = false;
    }

    function markImageAsDeleted() {
        const confirmation = confirm('Är du säker på att du vill ta bort denna bild?');

        if (!confirmation) {
            return;
        }

        _image.image = null;
        _image.image_crop = null;
        _image.file = null;
        _image.cropper = null;
        _image.deleted = true;
    }

    watch(_image, function(currentValue) {
        emits('update:image', currentValue);
    });

    watch(_heading, function (currentValue) {
        emits('update:heading', currentValue);
    });

    watch(_desc, function (currentValue) {
        emits('update:desc', currentValue);
    });
</script>

<template>
    <div>
        <client-image
            ref="mainImageRef"
            @crop="onCrop" />

        <div class="flex items-center justify-center">
            <div class="max-w-md w-full relative">
                <div v-if="_image.image_crop" class="absolute right-[10px] top-[10px] bg-blue z-[9] rounded-md">
                    <div class="flex">
                        <button @click="mainImageRef.startFileUploading()" class="block border-r border-blue-600 px-2 py-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                            </svg>
                        </button>

                        <button @click="mainImageRef.startCropperWithImage(_image.image)" class="block border-r border-blue-600 px-2 py-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M9 3a1 1 0 012 0v5.5a.5.5 0 001 0V4a1 1 0 112 0v4.5a.5.5 0 001 0V6a1 1 0 112 0v5a7 7 0 11-14 0V9a1 1 0 012 0v2.5a.5.5 0 001 0V4a1 1 0 012 0v4.5a.5.5 0 001 0V3z" clip-rule="evenodd" />
                            </svg>
                        </button>

                        <button @click="markImageAsDeleted()" class="block px-2 py-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="aspect-w-4 aspect-h-3 border-2 border-secondaryBlue rounded-md overflow-hidden bg-white" :class="{
                    'border-opacity-0': _image.image_crop,
                    'border-dashed': !_image.image_crop
                }">
                    <img v-if="_image.image_crop" :src="_image.image_crop" />

                    <div v-if="!_image.image_crop" class="flex justify-center items-center">
                        <secondary-button
                            @click="mainImageRef.startFileUploading()">
                            Ladda upp bild
                        </secondary-button>
                    </div>
                </div>

                <div class="flex justify-center md:mt-[-75px] z-[50] relative">
                    <div class="md:hidden flex justify-center">
                        <div class="
                            z-[9] rounded-full bg-white overflow-hidden hover:cursor-pointer
                            w-[24vw] h-[24vw] mt-[-12vw]
                            ">
                            <div class="flex h-full justify-center items-center">
                                <img :src="$page.props.currentUser.company.logo" class="w-full" />
                            </div>
                        </div>
                    </div>

                    <div class="
                        hidden md:block border-opacity-0 bg-white
                        rounded-full bg-[#f8fafc] overflow-hidden hover:cursor-pointer
                        w-[150px] h-[150px]
                        border-2 border-secondaryBlue border-dashed
                        ">
                        <div class="flex h-full justify-center items-center">
                            <img :src="$page.props.currentUser.company.logo" class="w-full" />
                        </div>
                    </div>
                </div>

                <div class="my-4">
                    <input-field
                        v-model="_heading"
                        :error="errors.heading"
                        @input="$emit('errorResolved', 'heading')"
                        :tooltipEnabled="true"
                        label="Rubrik">

                        <template #tooltip>
                            Här ska du ange en kort och informativ rubrik som lockar till klick! Tex: "30% rabatt på höstskor online!" "Borås - Barn äter gratis på söndagar!". Använd ord som gör det superlätt att förstå innehållet i kampanjen.
                        </template>
                    </input-field>
                </div>

                <div class="mt-4">
                    <html-editor-field
                        v-model="_desc"
                        :error="errors.description"
                        :tooltipEnabled="true"
                        label="Beskrivning"
                        @input="$emit('errorResolved', 'description')">

                        <template #tooltip>
                            Här ska du ge mer information om kampanjen; Vad gäller kampanjen? Vilka villkor har den? Skriv med få ord och se till att det blir tydligt.
                        </template>
                    </html-editor-field>
                </div>
            </div>
        </div>
    </div>
</template>
